import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Archives.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// The Archives background image - mystical library corridor
const ARCHIVES_BG = "https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/9vfykjqd_Archives%20V2.png";

function Archives({ user, token }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [showCategoryBrowseModal, setShowCategoryBrowseModal] = useState(false);
  
  // Live data states
  const [stats, setStats] = useState({ total_entries: 0, contributors: 0, endorsed_entries: 0, categories: 0 });
  const [topContributors, setTopContributors] = useState([]);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [approvedEntries, setApprovedEntries] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  
  // Featured entry state
  const [featuredEntry, setFeaturedEntry] = useState(null);
  const [selectedFeaturedId, setSelectedFeaturedId] = useState('');
  
  // Category browse state
  const [browseCategory, setBrowseCategory] = useState(null);
  const [categoryEntries, setCategoryEntries] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [loadingCategoryEntries, setLoadingCategoryEntries] = useState(false);
  
  // Settings form state
  const [newCategory, setNewCategory] = useState('');
  const [blockUsername, setBlockUsername] = useState('');
  
  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Preview state for moderation
  const [previewEntry, setPreviewEntry] = useState(null);
  
  // Contribute form state
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    category: '',
    file: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Storage stats (Founder only)
  const [storageStats, setStorageStats] = useState(null);
  const [loadingStorage, setLoadingStorage] = useState(false);

  // Check if user can moderate (Steward, Aurel/Elder, Founder)
  const canModerate = ['Founder', 'Elder', 'Steward'].includes(user?.role);
  const isFounder = user?.role === 'Founder';

  // Fetch live stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token]);

  // Fetch top contributors
  const fetchTopContributors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/top-contributors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTopContributors(data.contributors);
      }
    } catch (err) {
      console.error('Error fetching contributors:', err);
    }
  }, [token]);

  // Fetch pending entries count (for moderators)
  const fetchPendingCount = useCallback(async () => {
    if (!canModerate) return;
    try {
      const response = await fetch(`${API_URL}/api/archives/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingEntries(data.entries);
        setPendingCount(data.entries.length);
      }
    } catch (err) {
      console.error('Error fetching pending:', err);
    }
  }, [token, canModerate]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [token]);

  // Fetch approved entries (for endorsement management)
  const fetchApprovedEntries = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setApprovedEntries(data.entries);
      }
    } catch (err) {
      console.error('Error fetching entries:', err);
    }
  }, [token]);

  // Fetch blocked users
  const fetchBlockedUsers = useCallback(async () => {
    if (!isFounder) return;
    try {
      const response = await fetch(`${API_URL}/api/archives/blocked-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlockedUsers(data.blocked_users);
      }
    } catch (err) {
      console.error('Error fetching blocked users:', err);
    }
  }, [token, isFounder]);

  // Fetch storage stats (Founder only)
  const fetchStorageStats = useCallback(async () => {
    if (!isFounder) return;
    setLoadingStorage(true);
    try {
      const response = await fetch(`${API_URL}/api/archives/storage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStorageStats(data);
      }
    } catch (err) {
      console.error('Error fetching storage stats:', err);
    }
    setLoadingStorage(false);
  }, [token, isFounder]);

  // Fetch featured entry
  const fetchFeaturedEntry = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/featured`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFeaturedEntry(data.featured);
        if (data.featured) {
          setSelectedFeaturedId(data.featured.id);
        }
      }
    } catch (err) {
      console.error('Error fetching featured entry:', err);
    }
  }, [token]);

  // Fetch entries by category
  const fetchCategoryEntries = useCallback(async (categoryId, search = '') => {
    setLoadingCategoryEntries(true);
    try {
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`${API_URL}/api/archives/entries/by-category/${categoryId}${searchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategoryEntries(data.entries);
      }
    } catch (err) {
      console.error('Error fetching category entries:', err);
    }
    setLoadingCategoryEntries(false);
  }, [token]);

  // Load data on mount
  useEffect(() => {
    fetchStats();
    fetchTopContributors();
    fetchPendingCount();
    fetchCategories();
    fetchFeaturedEntry();
  }, [fetchStats, fetchTopContributors, fetchPendingCount, fetchCategories, fetchFeaturedEntry]);

  // Set default category when categories load
  useEffect(() => {
    if (categories.length > 0 && !newEntry.category) {
      setNewEntry(prev => ({ ...prev, category: categories[0].id }));
    }
  }, [categories, newEntry.category]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }
    
    setSearching(true);
    setShowSearchDropdown(true);
    
    try {
      const response = await fetch(`${API_URL}/api/archives/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.entries || []);
      }
    } catch (err) {
      console.error('Error searching:', err);
    }
    setSearching(false);
  };

  const handleSelectEntry = async (entry) => {
    // Always fetch the full entry to ensure we have file_url and all data
    try {
      const response = await fetch(`${API_URL}/api/archives/entries/${entry.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const fullEntry = data.entry || data;
        setSelectedEntry({
          ...fullEntry,
          author_username: fullEntry.author_username || fullEntry.author_name || entry.author
        });
      } else {
        // Fallback to passed entry
        setSelectedEntry({
          ...entry,
          author_username: entry.author_name || entry.author_username || entry.author
        });
      }
    } catch (err) {
      console.error('Error fetching full entry:', err);
      setSelectedEntry({
        ...entry,
        author_username: entry.author_name || entry.author_username || entry.author
      });
    }
    setShowSearchDropdown(false);
    setSearchQuery('');
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage('');
    setUploadProgress(0);

    // Check file size before uploading (max 1GB)
    const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
    if (newEntry.file && newEntry.file.size > MAX_FILE_SIZE) {
      const sizeMB = (newEntry.file.size / (1024 * 1024)).toFixed(1);
      setSubmitMessage(`File size (${sizeMB}MB) exceeds maximum of 1GB. Please use a smaller file.`);
      setSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append('title', newEntry.title);
    formData.append('content', newEntry.content);
    formData.append('category', newEntry.category);
    if (newEntry.file) {
      formData.append('file', newEntry.file);
    }

    // Use XMLHttpRequest for progress tracking on file uploads
    if (newEntry.file && newEntry.file.size > 1024 * 1024) { // Only show progress for files > 1MB
      setIsUploading(true);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        setIsUploading(false);
        setUploadProgress(0);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          setSubmitMessage('Your entry has been submitted for review. A Steward, Aurel, or Founder will review it shortly.');
          setNewEntry({ title: '', content: '', category: categories[0]?.id || '', file: null });
          setTimeout(() => {
            setShowContributeModal(false);
            setSubmitMessage('');
          }, 3000);
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            setSubmitMessage(error.detail || 'Failed to submit entry');
          } catch {
            setSubmitMessage('Failed to submit entry');
          }
        }
        setSubmitting(false);
      });
      
      xhr.addEventListener('error', () => {
        setIsUploading(false);
        setUploadProgress(0);
        setSubmitMessage('An error occurred while uploading. Please try again.');
        setSubmitting(false);
      });
      
      xhr.open('POST', `${API_URL}/api/archives/entries`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
      
    } else {
      // For small files or no file, use regular fetch
      try {
        const response = await fetch(`${API_URL}/api/archives/entries`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          setSubmitMessage('Your entry has been submitted for review. A Steward, Aurel, or Founder will review it shortly.');
          setNewEntry({ title: '', content: '', category: categories[0]?.id || '', file: null });
          setTimeout(() => {
            setShowContributeModal(false);
            setSubmitMessage('');
          }, 3000);
        } else {
          const error = await response.json();
          setSubmitMessage(error.detail || 'Failed to submit entry');
        }
      } catch (err) {
        console.error('Upload error:', err);
        setSubmitMessage('An error occurred while submitting. If uploading a large file, try a smaller one.');
      }
      setSubmitting(false);
    }
  };

  const handleApprove = async (entryId) => {
    try {
      const response = await fetch(`${API_URL}/api/archives/entries/${entryId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPendingCount();
        fetchStats();
        fetchTopContributors();
      }
    } catch (err) {
      console.error('Error approving:', err);
    }
  };

  const handleReject = async (entryId) => {
    try {
      const response = await fetch(`${API_URL}/api/archives/entries/${entryId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPendingCount();
      }
    } catch (err) {
      console.error('Error rejecting:', err);
    }
  };

  // Add new category (Founder only)
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/archives/categories`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCategory.trim() })
      });
      if (response.ok) {
        setNewCategory('');
        fetchCategories();
      }
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  // Remove category (Founder only)
  const handleRemoveCategory = async (categoryId) => {
    try {
      const response = await fetch(`${API_URL}/api/archives/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchCategories();
      }
    } catch (err) {
      console.error('Error removing category:', err);
    }
  };

  // Block user from Archives (Founder only)
  const handleBlockUser = async () => {
    if (!blockUsername.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/archives/block-user`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username: blockUsername.trim() })
      });
      if (response.ok) {
        setBlockUsername('');
        fetchBlockedUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to block user');
      }
    } catch (err) {
      console.error('Error blocking user:', err);
    }
  };

  // Unblock user (Founder only)
  const handleUnblockUser = async (username) => {
    try {
      const response = await fetch(`${API_URL}/api/archives/unblock-user`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username })
      });
      if (response.ok) {
        fetchBlockedUsers();
      }
    } catch (err) {
      console.error('Error unblocking user:', err);
    }
  };

  // Toggle endorsement on entry
  const handleToggleEndorse = async (entryId) => {
    try {
      const response = await fetch(`${API_URL}/api/archives/entries/${entryId}/endorse`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchApprovedEntries();
        fetchStats();
      }
    } catch (err) {
      console.error('Error toggling endorsement:', err);
    }
  };

  // Delete entry (Founder only)
  const handleDeleteEntry = async (entryId, entryTitle) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${entryTitle}" from the Archives? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/archives/entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchApprovedEntries();
        fetchStats();
        fetchTopContributors();
      }
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  // Open settings modal
  const openSettings = () => {
    fetchCategories();
    fetchBlockedUsers();
    fetchApprovedEntries();
    setShowSettingsModal(true);
  };

  // Set featured entry (Founder only)
  const handleSetFeaturedEntry = async () => {
    try {
      const response = await fetch(`${API_URL}/api/archives/featured`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ entry_id: selectedFeaturedId || null })
      });
      if (response.ok) {
        fetchFeaturedEntry();
      }
    } catch (err) {
      console.error('Error setting featured entry:', err);
    }
  };

  // Open category browse modal
  const openCategoryBrowse = (category) => {
    setBrowseCategory(category);
    setCategorySearch('');
    setCategoryEntries([]);
    setShowCategoryBrowseModal(true);
    fetchCategoryEntries(category.id);
  };

  // Handle category search
  const handleCategorySearch = (search) => {
    setCategorySearch(search);
    if (browseCategory) {
      fetchCategoryEntries(browseCategory.id, search);
    }
  };

  return (
    <div className="archives-page" data-testid="archives-page">
      {/* Background image */}
      <div className="archives-background">
        <img 
          src={ARCHIVES_BG}
          alt="The Archives"
          className="archives-bg-image"
        />
        <div className="archives-overlay"></div>
      </div>

      {/* Header */}
      <header className="archives-header visible">
        <button 
          className="archives-back-btn"
          onClick={() => navigate('/guild-hall')}
          data-testid="back-to-guild"
        >
          <span className="back-arrow">←</span>
          <span className="back-text">Return to Guild Hall</span>
        </button>
        
        <div className="archives-title-area">
          <h1 className="archives-title">THE ARCHIVES</h1>
          <p className="archives-subtitle">Where knowledge is eternal. Seek freely. Preserve wisely.</p>
        </div>
        
        <div className="archives-search-area">
          {isFounder && (
            <button 
              className="header-settings-btn"
              onClick={openSettings}
              data-testid="archives-settings-btn"
            >
              <span className="settings-icon">⚙</span>
            </button>
          )}
          <form className="archives-search-form" onSubmit={e => e.preventDefault()}>
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search the Archives..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowSearchDropdown(true)}
                onKeyDown={(e) => e.key === 'Escape' && setShowSearchDropdown(false)}
                className="archives-search-input"
                data-testid="archives-search"
              />
              {/* Search Results Dropdown */}
              {showSearchDropdown && (
                <div className="search-dropdown" onMouseDown={(e) => e.preventDefault()}>
                  {searching ? (
                    <div className="search-loading">Searching...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="search-empty">No entries found matching "{searchQuery}"</div>
                  ) : (
                    searchResults.map(entry => (
                      <div 
                        key={entry.id} 
                        className="search-result-item"
                        onClick={() => handleSelectEntry(entry)}
                      >
                        <div className="search-result-title">
                          {entry.title}
                          {entry.endorsed && <span className="endorsed-star">★</span>}
                        </div>
                        <div className="search-result-meta">
                          <span className="search-result-category">{entry.category}</span>
                          <span className="search-result-author">by {entry.author_username}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </form>
          <button 
            className={`archives-filters-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            data-testid="archives-filters-btn"
          >
            <span className="filters-icon">⚙</span>
            <span className="filters-text">FILTERS</span>
          </button>
        </div>
      </header>

      {/* Filters Panel */}
      {showFilters && (
        <div className="archives-filters-panel" data-testid="archives-filters-panel">
          <div className="filters-content">
            <h3>Filter Archives</h3>
            <div className="filter-group">
              <label>Category</label>
              <select className="filter-select">
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Sort By</label>
              <select className="filter-select">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="popular">Most Popular</option>
                <option value="endorsed">Most Endorsed</option>
              </select>
            </div>
            <button className="apply-filters-btn" onClick={() => setShowFilters(false)}>
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Right Sidebar */}
      <aside className="archives-sidebar">
        {/* Archives at a Glance */}
        <div className="sidebar-card glance-card">
          <h3 className="sidebar-card-title">The Archives at a Glance</h3>
          <div className="glance-stats">
            <div className="glance-stat">
              <span className="glance-value">{stats.total_entries}</span>
              <span className="glance-label">Total Entries</span>
            </div>
            <div className="glance-stat">
              <span className="glance-value">{stats.contributors}</span>
              <span className="glance-label">Contributors</span>
            </div>
            <div className="glance-stat">
              <span className="glance-value">{stats.endorsed_entries}</span>
              <span className="glance-label">Endorsed Entries</span>
            </div>
            <div className="glance-stat">
              <span className="glance-value">{categories.length}</span>
              <span className="glance-label">Categories</span>
            </div>
          </div>
        </div>

        {/* Featured Scroll */}
        <div className="sidebar-card featured-card">
          <h3 className="sidebar-card-title">Featured Scroll</h3>
          {featuredEntry ? (
            <div 
              className="featured-scroll clickable"
              onClick={() => {
                // Fetch the full entry
                handleSelectEntry({
                  id: featuredEntry.id,
                  title: featuredEntry.title,
                  content: featuredEntry.content,
                  category: featuredEntry.category,
                  author: featuredEntry.author,
                  endorsed: featuredEntry.endorsed
                });
              }}
            >
              <div className="featured-icon">📜</div>
              <div className="featured-info">
                <h4 className="featured-name">{featuredEntry.title}</h4>
                <p className="featured-desc">{featuredEntry.content}</p>
                {featuredEntry.endorsed && <span className="featured-badge">Endorsed</span>}
              </div>
            </div>
          ) : (
            <div className="featured-scroll empty">
              <div className="featured-icon">📜</div>
              <div className="featured-info">
                <p className="featured-desc">No featured scroll selected yet.</p>
              </div>
            </div>
          )}
        </div>

        {/* Browse by Category */}
        <div className="sidebar-card categories-card">
          <h3 className="sidebar-card-title">Browse by Category</h3>
          <div className="category-buttons">
            {categories.map(cat => (
              <button 
                key={cat.id} 
                className="category-browse-btn"
                onClick={() => openCategoryBrowse(cat)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Top Contributors */}
        <div className="sidebar-card contributors-card">
          <h3 className="sidebar-card-title">Top Contributors</h3>
          {topContributors.length > 0 ? (
            <ul className="contributors-list">
              {topContributors.map((contributor, index) => (
                <li key={contributor.name || contributor.id || index} className="contributor-item">
                  <span className="contributor-rank">{index + 1}</span>
                  <span className="contributor-name">{contributor.name}</span>
                  <span className="contributor-entries">{contributor.entries} {contributor.entries === 1 ? 'entry' : 'entries'}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-contributors">No contributors yet. Be the first!</p>
          )}
        </div>

        {/* Archivist Quote */}
        <div className="sidebar-card quote-card">
          <div className="sidebar-quote">
            <p className="sidebar-quote-text">
              "Knowledge is not owned. It is preserved. So that it may outlive us all."
            </p>
            <p className="sidebar-quote-author">— The Archivist</p>
          </div>
        </div>
      </aside>

      {/* Floating Action Buttons - Bottom Left */}
      <div className="archives-action-buttons">
        <button 
          className="contribute-btn"
          onClick={() => setShowContributeModal(true)}
          data-testid="contribute-btn"
        >
          <span className="contribute-icon">✍</span>
          <span>Contribute Entry</span>
        </button>

        {canModerate && (
          <button 
            className="moderation-btn"
            onClick={() => setShowPendingModal(true)}
            data-testid="moderation-btn"
          >
            <span className="moderation-icon">📋</span>
            <span>Review Entries</span>
            {pendingCount > 0 && (
              <span className="pending-badge">{pendingCount}</span>
            )}
          </button>
        )}
      </div>

      {/* Center content area */}
      <div className="archives-center">
        {/* Entries will be displayed here in future */}
      </div>

      {/* Contribute Modal */}
      {showContributeModal && (
        <div className="archives-modal-overlay" onClick={() => setShowContributeModal(false)}>
          <div className="archives-modal contribute-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Contribute to the Archives</h2>
              <button className="modal-close" onClick={() => setShowContributeModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitEntry} className="contribute-form">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newEntry.title}
                  onChange={e => setNewEntry({...newEntry, title: e.target.value})}
                  placeholder="Enter a title for your entry..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={newEntry.category}
                  onChange={e => setNewEntry({...newEntry, category: e.target.value})}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={newEntry.content}
                  onChange={e => setNewEntry({...newEntry, content: e.target.value})}
                  placeholder="Share your knowledge with the Archives..."
                  rows={8}
                  required
                />
              </div>
              <div className="form-group">
                <label>Attachment (optional, up to 1GB)</label>
                <input
                  type="file"
                  onChange={e => setNewEntry({...newEntry, file: e.target.files[0]})}
                  className="file-input"
                />
                {newEntry.file && (
                  <div className="file-info">
                    📎 {newEntry.file.name} ({(newEntry.file.size / (1024 * 1024)).toFixed(1)} MB)
                  </div>
                )}
              </div>
              {/* Upload Progress Bar */}
              {isUploading && (
                <div className="upload-progress-container" data-testid="upload-progress">
                  <div className="upload-progress-label">
                    Uploading... {uploadProgress}%
                  </div>
                  <div className="upload-progress-bar">
                    <div 
                      className="upload-progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
              {submitMessage && (
                <div className={`submit-message ${submitMessage.includes('submitted') ? 'success' : 'error'}`}>
                  {submitMessage}
                </div>
              )}
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowContributeModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </button>
              </div>
              <p className="review-note">
                All entries are reviewed by a Steward, Aurel, or Founder before being added to the Archives.
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Pending Entries Modal (for Moderators) */}
      {showPendingModal && canModerate && (
        <div className="archives-modal-overlay" onClick={() => setShowPendingModal(false)}>
          <div className="archives-modal pending-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Entries Awaiting Review</h2>
              <button className="modal-close" onClick={() => setShowPendingModal(false)}>×</button>
            </div>
            <div className="pending-entries-list">
              {pendingEntries.length === 0 ? (
                <p className="no-pending">No entries awaiting review.</p>
              ) : (
                pendingEntries.map(entry => (
                  <div key={entry.id} className="pending-entry">
                    <div className="pending-entry-header">
                      <h3>{entry.title}</h3>
                      <span className="pending-category">{entry.category}</span>
                    </div>
                    <p className="pending-author">By {entry.author_username}</p>
                    <p className="pending-content">{entry.content}</p>
                    {entry.file_url && (
                      <div className="pending-file-section">
                        <p className="pending-file-label">📎 Attached Document: {entry.file_name || 'File'}</p>
                        <a 
                          href={`${API_URL}${entry.file_url}?authorization=Bearer ${token}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-document-btn"
                        >
                          👁 Preview Document
                        </a>
                      </div>
                    )}
                    <div className="pending-actions">
                      <button 
                        className="approve-btn"
                        onClick={() => handleApprove(entry.id)}
                      >
                        ✓ Approve
                      </button>
                      <button 
                        className="reject-btn"
                        onClick={() => handleReject(entry.id)}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Archives Settings Modal (Founder only) */}
      {showSettingsModal && isFounder && (
        <div className="archives-modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="archives-modal settings-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Archives Settings</h2>
              <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>
            </div>
            <div className="settings-content">
              {/* Featured Scroll Selection */}
              <section className="settings-section">
                <h3>Featured Scroll</h3>
                <p className="section-desc">Select an approved entry to feature on the Archives page</p>
                <div className="featured-selection-form">
                  <select
                    value={selectedFeaturedId}
                    onChange={e => setSelectedFeaturedId(e.target.value)}
                    className="featured-select"
                  >
                    <option value="">-- No Featured Scroll --</option>
                    {approvedEntries.map(entry => (
                      <option key={entry.id} value={entry.id}>
                        {entry.title} {entry.endorsed ? '⭐' : ''} - by {entry.author_name}
                      </option>
                    ))}
                  </select>
                  <button onClick={handleSetFeaturedEntry} className="set-featured-btn">
                    Set Featured
                  </button>
                </div>
                {featuredEntry && (
                  <p className="current-featured">
                    Currently featuring: <strong>{featuredEntry.title}</strong>
                  </p>
                )}
              </section>

              {/* Category Management */}
              <section className="settings-section">
                <h3>Category Management</h3>
                <div className="add-category-form">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    placeholder="New category name..."
                  />
                  <button onClick={handleAddCategory} className="add-btn">+ Add</button>
                </div>
                <div className="categories-list">
                  {categories.map(cat => (
                    <div key={cat.id} className="category-item">
                      <span>{cat.name}</span>
                      <button 
                        className="remove-btn"
                        onClick={() => handleRemoveCategory(cat.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {categories.length === 0 && (
                    <p className="empty-text">No categories yet. Add one above.</p>
                  )}
                </div>
              </section>

              {/* Blocked Users */}
              <section className="settings-section">
                <h3>Blocked Users</h3>
                <p className="section-desc">Block users from contributing to the Archives</p>
                <div className="block-user-form">
                  <input
                    type="text"
                    value={blockUsername}
                    onChange={e => setBlockUsername(e.target.value)}
                    placeholder="Username to block..."
                  />
                  <button onClick={handleBlockUser} className="block-btn">Block</button>
                </div>
                <div className="blocked-list">
                  {blockedUsers.map(username => (
                    <div key={username} className="blocked-item">
                      <span>{username}</span>
                      <button 
                        className="unblock-btn"
                        onClick={() => handleUnblockUser(username)}
                      >
                        Unblock
                      </button>
                    </div>
                  ))}
                  {blockedUsers.length === 0 && (
                    <p className="empty-text">No blocked users.</p>
                  )}
                </div>
              </section>

              {/* Storage Dashboard */}
              <section className="settings-section storage-section">
                <h3>📊 Storage Dashboard</h3>
                <p className="section-desc">Monitor your Archives storage usage</p>
                <button 
                  onClick={fetchStorageStats} 
                  className="refresh-storage-btn"
                  disabled={loadingStorage}
                >
                  {loadingStorage ? 'Loading...' : '🔄 Refresh Stats'}
                </button>
                {storageStats && (
                  <div className="storage-stats-grid">
                    <div className="storage-stat-card total">
                      <div className="stat-value">{storageStats.total_storage_formatted}</div>
                      <div className="stat-label">Total Storage Used</div>
                    </div>
                    <div className="storage-stat-card">
                      <div className="stat-value">{storageStats.total_files}</div>
                      <div className="stat-label">Total Files</div>
                    </div>
                    <div className="storage-stat-card">
                      <div className="stat-value">{storageStats.gridfs_formatted}</div>
                      <div className="stat-label">New Files (GridFS)</div>
                    </div>
                    <div className="storage-stat-card">
                      <div className="stat-value">{storageStats.legacy_files}</div>
                      <div className="stat-label">Legacy Files</div>
                    </div>
                    {storageStats.largest_files && storageStats.largest_files.length > 0 && (
                      <div className="largest-files-section">
                        <h4>Largest Files</h4>
                        <ul className="largest-files-list">
                          {storageStats.largest_files.map((file, idx) => (
                            <li key={idx}>
                              <span className="file-name">{file.filename}</span>
                              <span className="file-size">{file.size_formatted}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Manage Entries */}
              <section className="settings-section">
                <h3>Manage Entries</h3>
                <p className="section-desc">View all entries, manage endorsements, and remove entries if needed</p>
                <div className="entries-list">
                  {approvedEntries.map(entry => (
                    <div key={entry.id} className={`entry-item ${entry.endorsed ? 'entry-endorsed' : ''}`}>
                      <div className="entry-info">
                        <div className="entry-title-row">
                          <span className="entry-title">{entry.title}</span>
                          {entry.endorsed && <span className="endorsed-badge">★ Endorsed</span>}
                        </div>
                        <span className="entry-author">by {entry.author_username}</span>
                        <span className="entry-category">{entry.category}</span>
                      </div>
                      <div className="entry-actions">
                        <button 
                          className={`endorse-btn ${entry.endorsed ? 'endorsed' : ''}`}
                          onClick={() => handleToggleEndorse(entry.id)}
                          title={entry.endorsed ? 'Remove endorsement' : 'Endorse this entry'}
                        >
                          {entry.endorsed ? '★' : '☆'}
                        </button>
                        <button 
                          className="delete-entry-btn"
                          onClick={() => handleDeleteEntry(entry.id, entry.title)}
                          title="Delete this entry"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  ))}
                  {approvedEntries.length === 0 && (
                    <p className="empty-text">No entries to manage yet.</p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Entry Detail Popup */}
      {selectedEntry && (
        <div className="archives-modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="archives-modal entry-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {selectedEntry.title}
                {selectedEntry.endorsed && <span className="endorsed-badge-large">★ Endorsed</span>}
              </h2>
              <button className="modal-close" onClick={() => setSelectedEntry(null)}>×</button>
            </div>
            <div className="entry-detail-content">
              <div className="entry-detail-meta">
                <span className="detail-category">{selectedEntry.category}</span>
                <span className="detail-author">Contributed by <strong>{selectedEntry.author_username || selectedEntry.author_name || selectedEntry.author || 'Unknown'}</strong></span>
                {selectedEntry.created_at && (
                  <span className="detail-date">
                    {new Date(selectedEntry.created_at).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                )}
              </div>
              <div className="entry-detail-description">
                <h3>Description</h3>
                <p>{selectedEntry.content}</p>
              </div>
              {selectedEntry.file_url && (
                <div className="entry-detail-file">
                  <h3>Attached Document</h3>
                  <a 
                    href={`${API_URL}${selectedEntry.file_url}?authorization=Bearer ${token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="view-file-btn"
                  >
                    📄 Open {selectedEntry.file_name || 'Document'}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Category Browse Modal */}
      {showCategoryBrowseModal && browseCategory && (
        <div className="archives-modal-overlay" onClick={() => setShowCategoryBrowseModal(false)}>
          <div className="archives-modal category-browse-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{browseCategory.name}</h2>
              <button className="modal-close" onClick={() => setShowCategoryBrowseModal(false)}>×</button>
            </div>
            <div className="category-browse-search">
              <input
                type="text"
                value={categorySearch}
                onChange={e => handleCategorySearch(e.target.value)}
                placeholder={`Search in ${browseCategory.name}...`}
                className="category-search-input"
              />
            </div>
            <div className="category-entries-list">
              {loadingCategoryEntries ? (
                <p className="loading-text">Loading entries...</p>
              ) : categoryEntries.length === 0 ? (
                <p className="no-entries-text">
                  {categorySearch ? 'No entries match your search.' : 'No entries in this category yet.'}
                </p>
              ) : (
                categoryEntries.map(entry => (
                  <div 
                    key={entry.id} 
                    className={`category-entry-item ${entry.endorsed ? 'endorsed' : ''}`}
                    onClick={() => {
                      setShowCategoryBrowseModal(false);
                      handleSelectEntry(entry);
                    }}
                  >
                    <div className="category-entry-header">
                      <h4 className="category-entry-title">{entry.title}</h4>
                      {entry.endorsed && <span className="endorsed-badge">⭐ Endorsed</span>}
                    </div>
                    <p className="category-entry-preview">{entry.content}</p>
                    <div className="category-entry-meta">
                      <span className="category-entry-author">By {entry.author}</span>
                      {entry.has_file && <span className="has-file-badge">📎 Has attachment</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Archives;
